import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import {
  Users,
  Building2,
  Clock,
  ShieldCheck,
  Globe,
  Workflow,
  Cloud,
  ArrowRight,
} from "lucide-react";

const services = [
  {
    icon: Users,
    title: "CRM Development",
    description:
      "Custom CRM systems designed for call centers to manage leads, track interactions, and boost sales performance.",
    gradient: "from-blue-500 to-cyan-500",
    hoverGlow: "rgba(59, 130, 246, 0.3)",
  },
  {
    icon: Building2,
    title: "HRMS & Payroll Systems",
    description:
      "Comprehensive HR management solutions including employee tracking, payroll processing, and performance management.",
    gradient: "from-purple-500 to-pink-500",
    hoverGlow: "rgba(147, 51, 234, 0.3)",
  },
  {
    icon: Clock,
    title: "Attendance & Biometric",
    description:
      "Advanced attendance tracking with biometric integrations, shift management, and real-time reporting.",
    gradient: "from-orange-500 to-red-500",
    hoverGlow: "rgba(249, 115, 22, 0.3)",
  },
  {
    icon: ShieldCheck,
    title: "DNC Scrubber & Compliance",
    description:
      "Ensure regulatory compliance with our DNC scrubber software and compliance management tools.",
    gradient: "from-green-500 to-emerald-500",
    hoverGlow: "rgba(34, 197, 94, 0.3)",
  },
  {
    icon: Globe,
    title: "Custom Web Applications",
    description:
      "Tailored web applications built to address your unique business challenges and requirements.",
    gradient: "from-indigo-500 to-purple-500",
    hoverGlow: "rgba(99, 102, 241, 0.3)",
  },
  {
    icon: Workflow,
    title: "Workflow Automation",
    description:
      "Automate repetitive tasks and streamline operations with intelligent workflow solutions.",
    gradient: "from-cyan-500 to-blue-500",
    hoverGlow: "rgba(6, 182, 212, 0.3)",
  },
  {
    icon: Cloud,
    title: "Cloud-Based Solutions",
    description:
      "Scalable cloud software that grows with your business, accessible anywhere, anytime.",
    gradient: "from-pink-500 to-rose-500",
    hoverGlow: "rgba(236, 72, 153, 0.3)",
  },
];

const ServiceCard = ({ service, index, isInView }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / 20;
    const y = (e.clientY - rect.top - rect.height / 2) / 20;
    setMousePosition({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 0, y: 0 });
    setIsHovered(false);
  };

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: 0.1 * index }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      style={{
        transform: `perspective(1000px) rotateX(${-mousePosition.y}deg) rotateY(${mousePosition.x}deg)`,
        transition: 'transform 0.1s ease-out',
      }}
      className="service-card glass-card p-8 group cursor-pointer relative"
      data-testid={`service-card-${index}`}
    >
      {/* Hover glow effect */}
      <motion.div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${service.hoverGlow}, transparent 70%)`,
        }}
      />

      {/* Animated Icon */}
      <motion.div
        className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.gradient} p-0.5 mb-6 relative z-10`}
        whileHover={{ scale: 1.1, rotate: 5 }}
        transition={{ type: "spring", stiffness: 400 }}
      >
        <div className="w-full h-full bg-slate-900 rounded-2xl flex items-center justify-center relative overflow-hidden">
          <motion.div
            animate={isHovered ? { rotate: 360 } : { rotate: 0 }}
            transition={{ duration: 0.5 }}
          >
            <service.icon className="text-white relative z-10" size={24} />
          </motion.div>
          {/* Icon shine effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={isHovered ? { x: '100%' } : { x: '-100%' }}
            transition={{ duration: 0.6 }}
          />
        </div>
      </motion.div>

      {/* Content */}
      <motion.h3 
        className="text-xl font-semibold text-white mb-3 group-hover:text-blue-400 transition-colors relative z-10"
        animate={isHovered ? { x: 5 } : { x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {service.title}
      </motion.h3>
      <p className="text-slate-400 leading-relaxed relative z-10">
        {service.description}
      </p>

      {/* Learn more link */}
      <motion.div
        className="mt-4 flex items-center gap-2 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity relative z-10"
        animate={isHovered ? { x: 5 } : { x: 0 }}
      >
        <span className="text-sm font-medium">Learn more</span>
        <motion.div
          animate={isHovered ? { x: [0, 5, 0] } : { x: 0 }}
          transition={{ duration: 0.8, repeat: Infinity }}
        >
          <ArrowRight size={14} />
        </motion.div>
      </motion.div>

      {/* Corner decoration */}
      <motion.div
        className="absolute bottom-0 right-0 w-20 h-20 opacity-20"
        initial={{ scale: 0, rotate: 0 }}
        animate={isHovered ? { scale: 1, rotate: 45 } : { scale: 0, rotate: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className={`w-full h-full bg-gradient-to-br ${service.gradient} rounded-tl-full`} />
      </motion.div>
    </motion.div>
  );
};

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section
      id="services"
      ref={ref}
      className="relative py-24 md:py-32 bg-slate-950/50 overflow-hidden"
      data-testid="services-section"
    >
      {/* Animated background elements */}
      <motion.div
        className="absolute top-20 left-10 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.2, 1],
          x: [0, 30, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.3, 1],
          y: [0, -30, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span 
            className="text-sm uppercase tracking-widest text-blue-400 font-medium mb-4 block"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            Our Services
          </motion.span>
          <motion.h2 
            className="text-3xl md:text-5xl font-bold tracking-tight mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
          >
            Solutions That <span className="text-gradient">Drive Results</span>
          </motion.h2>
          <motion.p 
            className="text-lg text-slate-400 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
          >
            We offer a comprehensive suite of software solutions designed specifically 
            for call centers and BPO companies.
          </motion.p>
        </motion.div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <ServiceCard
              key={service.title}
              service={service}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-16"
        >
          <motion.a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#contact").scrollIntoView({ behavior: "smooth" });
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 py-4 font-medium transition-all duration-300 btn-glow group"
            data-testid="services-cta"
          >
            Discuss Your Project
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <ArrowRight size={18} />
            </motion.div>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
